import { useOnClickOutside } from '@flexo/hooks';
import { Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAuth, useOrganization, useUser } from '@clerk/clerk-react';
import { IconWrapper, saveLang } from '@flexo/general';
import { RIGHT_DRAWER, USER } from '@flexo/reducers';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styles from './profile-button.module.scss';

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const ENV = (import.meta as any).env;

export const ProfileButton: React.FC = () => {
  const {
    ProfileButton,
    ProfileButton__Wrapper,
    ProfileButton__Popper,
    ProfileButton__Popper__Item,
    ProfileButton__Popper__Item__LogOut,
    ProfileButton__Popper__Item__DisplayName,
    ProfileButton__Popper__Item__Email,
    ProfileButton__Popper__Options__List,
    ProfileButton__Popper__Item__Option,
    ProfileButton__Popper__Item__Option__Text,
    Clickable,
  } = styles;

  const menuRef = React.useRef(null);

  const [open, setOpen] = useState<boolean>(false);
  const [showLanguageSelector, setShowLanguageSelector] =
    useState<boolean>(false);
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const kpi = useSelector((state: any) => state.kpi);
  const metadata = useSelector((state: any) => state.metadata);
  const { signOut: clerkSignOut } = useAuth();
  const { user: clerkUser } = useUser();
  const { organization, membership } = useOrganization();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Check if user is admin via Clerk organization membership
  const isAdmin = membership?.role === 'org:admin';
  // FLEXO-6067: end users get an Invoices entry (staff manage payments elsewhere).
  const isEndUser = membership?.role === 'org:end_user';

  const availableLanguages = t('dropdowns.profile.languages', {
    returnObjects: true,
  });

  function isChildOf(event) {
    const parentDiv = document.getElementById('profile-button');
    let targetElement = event.target; // L'elemento che è stato cliccato

    // Itera attraverso i genitori dell'elemento target fino a raggiungere il body
    while (targetElement !== document.body) {
      if (targetElement === parentDiv) {
        return true; // Il target è figlio del parentDiv
      }
      targetElement = targetElement.parentNode; // Vai al genitore dell'elemento target
    }

    return false; // Il target non è figlio del parentDiv
  }

  useOnClickOutside(menuRef, (ev: any) => {
    if (open && isChildOf(ev) === false) {
      setOpen(false);
      setShowLanguageSelector(false);
    }
  });

  // FLEXO-6133 #6e: Escape closes the menu (and resets the submenu), like
  // any other dismissible overlay.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setOpen(false);
        setShowLanguageSelector(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const handleClick = async () => {
    setOpen(!open);
    // FLEXO-6133 #6c: always reopen on the root menu — without this, a menu
    // closed while on the Language submenu reopened straight on it.
    setShowLanguageSelector(false);
  };

  const onClickLogOut = async () => {
    console.log('[ProfileButton] Logout clicked - starting logout flow');
    dispatch(USER.SetIsLoggingOut());
    dispatch(USER.ShowSplashScreen());
    setOpen(false);

    try {
      // Call Clerk signOut
      console.log('[ProfileButton] Calling Clerk signOut...');
      await clerkSignOut();
      console.log('[ProfileButton] Clerk signOut complete');

      // Trigger Redux-based logout flow (for app.tsx to handle navigation)
      dispatch(USER.SetLogout());
    } catch (error) {
      console.error('[ProfileButton] Logout error:', error);
      // Even if there's an error, try to logout via Redux
      dispatch(USER.SetLogout());
    }
  };

  const onClickProfile = () => {
    dispatch(
      RIGHT_DRAWER.SetDrawer({
        type: 'CREDENTIALS_UPDATE_FORM',
        show: true,
      } as any)
    );
  };

  const onClickFeedback = () => {
    dispatch(
      RIGHT_DRAWER.SetDrawer({
        type: 'FEEDBACK_FORM',
        show: true,
      } as any)
    );
  };
  const onClickInformations = () => {
    dispatch(
      RIGHT_DRAWER.SetDrawer({
        type: 'INFORMATIONS_PAGE',
        show: true,
      } as any)
    );
  };

  // FLEXO-6067: open the invoices list drawer (reuses PaymentsListDrawerContent).
  // The drawer's overview table reads `drawerConfig.data`, so build it from the
  // loaded kwick invoices (same shape as the PaymentsList widget) — never pass an
  // undefined drawerConfig, which would crash the table.
  const onClickInvoices = () => {
    // FLEXO-6075 #2: open invoices as the mobile single-list (Billing) instead of
    // the period/details table. Billing reads kpi.kwickInvoices itself.
    dispatch(
      RIGHT_DRAWER.SetDrawer({
        type: 'ENDUSER_INVOICES',
        show: true,
        drawerConfig: {
          // Plain label (the table's title carries <strong> markup meant for HTML
          // rendering, which would show as literal tags in the list drawer header).
          title: t('dropdowns.profile.invoices') as string,
        },
      } as any)
    );
  };

  const onClickLanguage = () => {
    setShowLanguageSelector(true);
  };

  function onClickLanguageItem(language: string) {
    i18n.changeLanguage(language);
    saveLang(language);
  }

  const onClickAdminManagement = () => {
    if (isAdmin) {
      navigate('/admin-management');
    }
  };

  const isAdminPage = window.location.pathname.includes('/admin-management');

  return (
    <div
      key={`${ENV.VITE_APP_ORG}_profile-button`}
      className={ProfileButton__Wrapper}
      ref={menuRef}
      id="profile-button"
    >
      <Button
        id="profile-button"
        className={ProfileButton}
        type="button"
        onClick={handleClick}
        aria-label={t('dropdowns.profile.open_menu_label') as string}
      >
        <IconWrapper iconName="user_outline" />
      </Button>
      {open && (
        <div className={ProfileButton__Popper}>
          {showLanguageSelector ? (
            // Language selector content
            <div className={styles.ProfileButton__LanguageContent}>
              <span
                onClick={() => setShowLanguageSelector(false)}
                className={styles.ProfileButton__LanguageContent__Back}
              >
                <IconWrapper iconName="chevron-left" />
                {t('dropdowns.profile.back_label')}
              </span>
              <p className={styles.ProfileButton__LanguageContent__Title}>
                {t('dropdowns.profile.set_language')}
              </p>
              {Object.keys(availableLanguages)
                .sort((a, b) => a.localeCompare(b))
                .map((language: any) => (
                  <p
                    key={`${ENV.VITE_APP_ORG}_${language}`}
                    className={`${styles.ProfileButton__LanguageContent__Item} ${
                      `${ENV.VITE_APP_ORG}_${language}` === i18n.language
                        ? styles.ProfileButton__LanguageContent__Item__Active
                        : ''
                    }`}
                    onClick={() =>
                      onClickLanguageItem(`${ENV.VITE_APP_ORG}_${language}`)
                    }
                  >
                    {t(`dropdowns.profile.languages.${language}`)}
                  </p>
                ))}
            </div>
          ) : (
            // Main profile content
            <>
              <ul key={`ProfileButton__Popper__Item___List`}>
                <li
                  key={`ProfileButton__Popper__Item__Profile`}
                  className={`${ProfileButton__Popper__Item} ${Clickable}`}
                  onClick={() => onClickProfile()}
                >
                  <p className={`${ProfileButton__Popper__Item__DisplayName}`}>
                    {clerkUser?.username ||
                      clerkUser?.fullName ||
                      [clerkUser?.firstName, clerkUser?.lastName]
                        .filter(Boolean)
                        .join(' ') ||
                      user?.displayName ||
                      '-'}
                  </p>
                  <p className={`${ProfileButton__Popper__Item__Email}`}>
                    {clerkUser?.primaryEmailAddress?.emailAddress ||
                      clerkUser?.emailAddresses?.[0]?.emailAddress ||
                      user?.email ||
                      '-'}
                  </p>
                </li>
                {isAdmin && (
                  <li
                    key={`ProfileButton__Popper__Item__admin_panel`}
                    className={`${ProfileButton__Popper__Item} ${Clickable}`}
                    onClick={() => onClickAdminManagement()}
                  >
                    <p
                      className={`${ProfileButton__Popper__Item__DisplayName} ${
                        isAdminPage ? 'color-primary-500' : ''
                      }`}
                    >
                      {t('dropdowns.profile.admin_panel')}
                    </p>
                    <p className={`${ProfileButton__Popper__Item__Email}`}>
                      {t('dropdowns.profile.admin_panel_text')}
                    </p>
                  </li>
                )}
                {isEndUser && (
                  <li
                    key={`ProfileButton__Popper__Item__Invoices`}
                    className={`${ProfileButton__Popper__Item} ${Clickable}`}
                    onClick={() => onClickInvoices()}
                  >
                    <p className={`${ProfileButton__Popper__Item__DisplayName}`}>
                      {t('dropdowns.profile.invoices')}
                    </p>
                    <p className={`${ProfileButton__Popper__Item__Email}`}>
                      {t('dropdowns.profile.invoices_text')}
                    </p>
                  </li>
                )}
                <li
                  key={`ProfileButton__Popper__Item__Support`}
                  className={`${ProfileButton__Popper__Item} ${Clickable}`}
                  onClick={() => onClickFeedback()}
                >
                  <p className={`${ProfileButton__Popper__Item__DisplayName}`}>
                    {t('dropdowns.profile.support')}
                  </p>
                  <p className={`${ProfileButton__Popper__Item__Email}`}>
                    {t('dropdowns.profile.report_problem')}
                  </p>
                </li>
                <li
                  key={`ProfileButton__Popper__Item__Informations`}
                  className={`${ProfileButton__Popper__Item} ${Clickable}`}
                  onClick={() => onClickInformations()}
                >
                  <p className={`${ProfileButton__Popper__Item__DisplayName}`}>
                    {t('dropdowns.profile.informations')}
                  </p>
                  <p className={`${ProfileButton__Popper__Item__Email}`}>
                    {t('dropdowns.profile.informations_text')}
                  </p>
                </li>
                <li
                  key={`ProfileButton__Popper__Item__Language`}
                  className={`${ProfileButton__Popper__Item} ${Clickable}`}
                  onClick={() => onClickLanguage()}
                >
                  <p className={`${ProfileButton__Popper__Item__DisplayName}`}>
                    {t('dropdowns.profile.language_label')}
                  </p>
                  <p className={`${ProfileButton__Popper__Item__Email}`}>
                    {t(
                      `dropdowns.profile.languages.${
                        i18next.language.split('_')[1]
                      }`
                    )}
                  </p>
                </li>
              </ul>
              <div
                key={`ProfileButton__Popper__Item_${uuidv4()}`}
                className={`${ProfileButton__Popper__Item__LogOut}`}
                onClick={() => onClickLogOut()}
              >
                {t('dropdowns.profile.logout')}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileButton;
